import AxiosFoundationAPI from "../../../../util/AxiosFoundationAPI";
import EnterpriseComponent from "../../EnterpriseComponent";
import {v4 as uuidv4} from "uuid";
import {V1ChangeOrderIDOCToArray} from "../../../../util/Payload";

// One approval round over many records, released as a unit (t1/v1/change_orders).
//
// NOT AN OBJECT. A change order is typed and form-driven like a record, but has no revision chain --
// it happens once and is not revised -- so it has its own table and its own component rather than
// riding on ObjectRevision.
//
// ITS APPROVAL IS THE ORDINARY ONE. Submitting and signing go through
// t1/v1/object_approval_requests with TargetType "ChangeOrder", the same endpoint a record uses, so
// there is nothing approval-shaped in here.
const URL = "t1/v1/change_orders";
const IDOC = "v1.ChangeOrder-001";

class ChangeOrderEnterpriseComponent extends EnterpriseComponent {

    // MANY STATES AT ONCE, which a scope cannot express. The query builder takes ONE equality per
    // attribute, so the lifecycle filter has to arrive as its own IN member -- the same mechanism
    // ObjectRevision uses for its status sidebar, and the reason that sidebar can tick five boxes.
    setStatesFilter(states) {
        this.statesFilter = Array.isArray(states) && states.length > 0 ? states : null;
    }

    getStatesFilter() {
        return this.statesFilter;
    }

    performQuery = (page, pageSize, foreignKey) => {
        page = page || 1;
        pageSize = pageSize || this.getDefaultCacheSize();

        this.notifyLoadStartListeners();

        const idocResponse = this.getIDOCResponse() ?? IDOC;

        const members = [];
        const queryPayload = this.prepareQueryPayload();
        if (queryPayload.size > 0) {
            for (const [key, value] of queryPayload) {
                if (key.includes("_query")) continue;
                let operator = "EQUAL";
                if (queryPayload.get(key + "_query") !== undefined) operator = queryPayload.get(key + "_query");
                members.push({"Type": "Attribute", "Name": key, "Value": value, "Operator": operator});
            }
        }

        if (this.statesFilter) {
            // IN takes a comma-separated STRING, not an array. Passing the array serialises as
            // "DRAFT,IN_REVIEW" only by accident of coercion, and not at all once a value contains
            // anything unusual. Joined explicitly, as ObjectRevision does.
            members.push({"Type": "Attribute", "Name": "State", "Value": this.statesFilter.join(","), "Operator": "IN"});
        }

        const QBE = {
            "PlatformMessage": {
                "-idoc": idocResponse,
                "-id": uuidv4(),
                "ChangeOrder": {
                    "_attributes": {"limit": pageSize, "sort": this.getFormattedSortSpecification(), "offset": (page - 1) * pageSize},
                    "QueryGroups": {"QueryGroup": [{"Name": "primary", "Operator": "AND", "Type": "QueryGroup", "Membership": {"Member": members}}]}
                }
            }
        };

        AxiosFoundationAPI({
            method: "GET",
            url: URL,
            json: true,
            params: {"QBE": btoa(JSON.stringify(QBE))},
            headers: {
                "visibility-mode": this.getVisibilityMode(),
                "visibility-value": this.getVisibilityValue(),
                "idoc-response": idocResponse,
            },
        }).then(response => {
            this.fetchEnterpriseComponentSuccess(V1ChangeOrderIDOCToArray(response.data), page, pageSize);
        }).catch(error => {
            this.fetchEnterpriseComponentFailed(error);
        });
    };

    buildPayload = () => {
        const r = {};
        r["USID"] = this.getAttributeValue("USID");
        r["ObjectTypeUSID"] = this.getAttributeValue("ObjectTypeUSID");
        r["Title"] = this.getAttributeValue("Title");
        r["Description"] = this.getAttributeValue("Description");
        r["Justification"] = this.getAttributeValue("Justification");
        r["ImpactAssessment"] = this.getAttributeValue("ImpactAssessment");
        r["EffectiveDatetime"] = this.getAttributeValue("EffectiveDatetime");
        r["ReleasedInSingleAction"] = this.getAttributeValue("ReleasedInSingleAction");
        r["PrimaryPositionUSID"] = this.getAttributeValue("PrimaryPositionUSID");
        r["PrimaryUserGroupUSID"] = this.getAttributeValue("PrimaryUserGroupUSID");
        r["PrimaryWorkGroupUSID"] = this.getAttributeValue("PrimaryWorkGroupUSID");
        // NEITHER THE IDENTIFIER NOR THE STATE IS SENT. Both belong to the server: the identifier
        // comes from the type's number pool, and the state moves by submitting, signing, closing or
        // voiding -- each of which does more than set a column, which is why the write event refuses
        // a bare change to it.
        return r;
    };

    enterpriseComponentPostIDOC = () => {
        const payload = {};
        payload.PlatformMessage = {};
        payload.PlatformMessage["-idoc"] = IDOC;
        payload.PlatformMessage.ChangeOrder = this.buildPayload();
        return payload;
    };

    performInsert() {
        const options = {method: "POST", url: URL, json: true, data: this.enterpriseComponentPostIDOC(),
                         headers: {"idoc-request": IDOC, "idoc-response": IDOC}};
        this.notifyInsertStartListeners();
        AxiosFoundationAPI(options)
            .then(response => { this.insertEnterpriseComponentSuccess(V1ChangeOrderIDOCToArray(response.data)); })
            .catch(error => { this.insertEnterpriseComponentFailed(error); });
    };

    performUpdate() {
        const payload = {};
        payload.PlatformMessage = {};
        payload.PlatformMessage["-idoc"] = IDOC;
        payload.PlatformMessage.ChangeOrder = this.preparePUTPayload();
        const options = {method: "PUT", url: URL, json: true, data: payload,
                         headers: {"idoc-request": IDOC, "idoc-response": IDOC}};
        this.notifyUpdateStartListeners();
        AxiosFoundationAPI(options)
            .then(response => { this.updateEnterpriseComponentSuccess(V1ChangeOrderIDOCToArray(response.data)); })
            .catch(error => { this.updateEnterpriseComponentFailed(error); });
    };

    /**
     * Close or Void, which are not edits.
     *
     * They travel on the same PUT as an edit but carry a LifecycleAction, because routing them
     * through the ordinary update would let a bare write to STATE skip everything else the
     * transition has to do -- releasing the members, or withdrawing the open approval request. The
     * server refuses a naked state change for exactly that reason.
     */
    performLifecycleAction(action, reason) {
        const payload = {};
        payload.PlatformMessage = {};
        payload.PlatformMessage["-idoc"] = IDOC;
        payload.PlatformMessage.ChangeOrder = {
            "USID": this.getAttributeValue("USID"),
            "LifecycleAction": action,
            ...(reason ? {"Reason": reason} : {}),
        };
        const options = {method: "PUT", url: URL, json: true, data: payload,
                         headers: {"idoc-request": IDOC, "idoc-response": IDOC}};
        this.notifyUpdateStartListeners();
        return AxiosFoundationAPI(options)
            .then(response => { this.updateEnterpriseComponentSuccess(V1ChangeOrderIDOCToArray(response.data)); })
            .catch(error => { this.updateEnterpriseComponentFailed(error); throw error; });
    };

    performDelete() {
        const payload = {};
        payload.PlatformMessage = {};
        payload.PlatformMessage["-idoc"] = IDOC;
        payload.PlatformMessage.ChangeOrder = {"USID": this.getAttributeValue("USID")};
        const options = {method: "DELETE", url: URL, json: true, data: payload,
                         headers: {"idoc-request": IDOC, "idoc-response": IDOC}};
        this.notifyDeleteStartListeners();
        AxiosFoundationAPI(options)
            .then(() => { this.deleteEnterpriseComponentSuccess(); })
            .catch(error => { this.deleteEnterpriseComponentFailed(error); });
    };
}

export default ChangeOrderEnterpriseComponent;
